import { useState, useCallback } from 'react';
import { CandidateSummary, CandidateStatus, ALLOWED_FILE_TYPES, MAX_FILE_SIZE, MAX_BATCH_SIZE } from '@recruitai/shared';
import { apiClient } from '../lib/api-client';

export interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  error?: string;
  key?: string;
  candidateId?: string;
}

interface UploadUrlResponse {
  uploadUrl: string;
  key: string;
  candidateId: string;
}

interface BatchUploadResponse {
  candidates: Array<{ candidateId: string; name: string; status: 'pending' }>;
}

export function useUploadResume(jobId: string) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (!(ALLOWED_FILE_TYPES as readonly string[]).includes(file.type)) {
      return 'Formato não suportado. Aceitos: PDF, DOC, DOCX';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Arquivo excede o limite de 10MB';
    }
    return null;
  }, []);

  const addFiles = useCallback((newFiles: File[]) => {
    setError(null);
    const total = files.length + newFiles.length;
    if (total > MAX_BATCH_SIZE) {
      setError(`Máximo de ${MAX_BATCH_SIZE} arquivos por lote`);
      return;
    }

    const uploadFiles: UploadFile[] = newFiles.map(file => {
      const validationError = validateFile(file);
      return {
        file,
        id: crypto.randomUUID(),
        status: validationError ? 'error' : 'pending',
        progress: 0,
        error: validationError || undefined,
      };
    });

    setFiles(prev => [...prev, ...uploadFiles]);
  }, [files.length, validateFile]);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const uploadFiles = useCallback(async (): Promise<CandidateSummary[]> => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return [];

    setUploading(true);
    setError(null);

    try {

      // 1. Para cada arquivo, gerar URL pré-assinada e fazer upload ao S3
      const uploadedFiles: UploadFile[] = [];

      for (const uf of pendingFiles) {
        try {
          // Atualizar status para uploading
          setFiles(prev => prev.map(f => f.id === uf.id ? { ...f, status: 'uploading' as const, progress: 10 } : f));

          // Gerar URL pré-assinada
          const fileType = uf.file.type || 'application/pdf';
          const urlResponse = await apiClient.post<UploadUrlResponse>(
            `/jobs/${jobId}/candidates/upload-url`,
            { fileName: uf.file.name, fileType }
          );

          setFiles(prev => prev.map(f => f.id === uf.id ? { ...f, progress: 30 } : f));

          // Upload direto ao S3
          await fetch(urlResponse.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': fileType },
            body: uf.file,
          });

          setFiles(prev => prev.map(f => f.id === uf.id ? {
            ...f, status: 'uploaded' as const, progress: 100,
            key: urlResponse.key, candidateId: urlResponse.candidateId,
          } : f));

          uploadedFiles.push({ ...uf, key: urlResponse.key, candidateId: urlResponse.candidateId });
        } catch (err: any) {
          setFiles(prev => prev.map(f => f.id === uf.id ? {
            ...f, status: 'error' as const, progress: 0, error: err.message || 'Falha no upload',
          } : f));
        }
      }

      if (uploadedFiles.length === 0) {
        setError('Nenhum arquivo foi enviado com sucesso');
        return [];
      }

      // 2. Registrar candidatos via batch-upload
      const candidates = uploadedFiles.map(uf => ({
        name: uf.file.name.replace(/\.[^.]+$/, ''),
        email: '',
        resumeS3Key: uf.key!,
        fileName: uf.file.name,
      }));

      const batchResponse = await apiClient.post<BatchUploadResponse>(
        `/jobs/${jobId}/candidates/batch-upload`,
        { candidates }
      );

      return batchResponse.candidates.map(c => ({
        candidateId: c.candidateId,
        name: c.name,
        email: '',
        status: CandidateStatus.PENDING,
        fitScore: 0,
        uploadedAt: new Date().toISOString(),
      }));
    } catch (err: any) {
      setError(err.message || 'Falha no upload');
      return [];
    } finally {
      setUploading(false);
    }
  }, [files, jobId]);

  const retryFile = useCallback((id: string) => {
    setFiles(prev => prev.map(f => f.id === id && f.status === 'error'
      ? { ...f, status: 'pending' as const, progress: 0, error: undefined }
      : f
    ));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setError(null);
  }, []);

  return { files, uploading, error, addFiles, removeFile, uploadFiles, retryFile, clearFiles };
}
